/**
 * Google Analytics 4 Provider
 */

import type { AnalyticsProvider, AnyAnalyticsEvent, UserTraits } from '../types';
import { sanitizeUrl } from '../privacy';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

export class GA4Provider implements AnalyticsProvider {
  name = 'Google Analytics 4';
  enabled: boolean;
  private debug: boolean;
  private measurementId: string;
  private scriptLoaded: boolean = false;
  private initPromise: Promise<void>;

  constructor(measurementId: string, debug: boolean = false) {
    this.measurementId = measurementId;
    this.debug = debug;
    this.enabled = false;
    
    // Initialize GA4 script
    this.initPromise = this.initializeScript();
    
    if (this.debug) {
      console.log(`[GA4] Initializing with measurement ID: ${measurementId}`);
    }
  }

  private async initializeScript(): Promise<void> {
    if (typeof window === 'undefined') return;
    
    // Check if script already exists
    const existingScript = document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`);
    if (existingScript) {
      // If script exists, check if gtag is available
      if (typeof window.gtag === 'function') {
        this.scriptLoaded = true;
        this.enabled = true;
        return;
      }
      // Wait a bit for gtag to be available
      await new Promise(resolve => setTimeout(resolve, 100));
      if (typeof window.gtag === 'function') {
        this.scriptLoaded = true;
        this.enabled = true;
        return;
      }
    }

    // Create and inject the Google Analytics script
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
      
      script.onload = () => {
        // Initialize gtag once script is loaded
        window.dataLayer = window.dataLayer || [];
        window.gtag = function() {
          window.dataLayer!.push(arguments);
        };
        window.gtag('js', new Date());
        window.gtag('config', this.measurementId);
        
        this.scriptLoaded = true;
        this.enabled = true;
        
        if (this.debug) {
          console.log('[GA4] Script loaded and initialized successfully');
        }
        resolve();
      };
      
      script.onerror = () => {
        if (this.debug) {
          console.error('[GA4] Failed to load Google Analytics script');
        }
        reject(new Error('Failed to load Google Analytics script'));
      };
      
      document.head.appendChild(script);
    });
  }

  private isAvailable(): boolean {
    return typeof window !== 'undefined' && typeof window.gtag === 'function' && this.scriptLoaded;
  }

  private async waitForInit(): Promise<boolean> {
    try {
      await this.initPromise;
      return this.isAvailable();
    } catch (error) {
      if (this.debug) {
        console.error('[GA4] Initialization failed:', error);
      }
      return false;
    }
  }

  private gtag(...args: any[]): void {
    if (!this.isAvailable()) return;
    
    if (this.debug) {
      console.log('[GA4] Event:', ...args);
    }
    
    window.gtag!(...args);
  }

  async track(event: AnyAnalyticsEvent): Promise<void> {
    if (!(await this.waitForInit())) return;

    // Map our event structure to GA4 format
    const eventName = this.formatEventName(event.event);
    const eventParams = this.mapEventProperties(event);

    this.gtag('event', eventName, eventParams);
  }

  async identify(userId: string, traits?: UserTraits): Promise<void> {
    if (!(await this.waitForInit())) return;

    // Set user properties in GA4
    this.gtag('set', {
      user_id: userId,
    });

    if (traits) {
      const userProperties = this.mapUserTraits(traits);
      this.gtag('set', 'user_properties', userProperties);
    }
  }

  async page(name: string, properties?: Record<string, any>): Promise<void> {
    if (!(await this.waitForInit())) return;

    const pageData = {
      page_title: name,
      page_location: properties?.url ? sanitizeUrl(properties.url) : window.location.href,
      page_path: properties?.path || window.location.pathname,
      ...this.sanitizeProperties(properties || {}),
    };

    this.gtag('event', 'page_view', pageData);
  }

  async reset(): Promise<void> {
    if (!(await this.waitForInit())) return;

    // Clear user ID in GA4
    this.gtag('set', {
      user_id: null,
    });
  }

  private formatEventName(eventName: string): string {
    // GA4 event names should be lowercase with underscores
    return eventName
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, '_')
      .replace(/_+/g, '_')
      .substring(0, 40); // GA4 limit
  }

  private mapEventProperties(event: AnyAnalyticsEvent): Record<string, any> {
    const baseParams = {
      event_category: event.category,
      event_label: event.event,
      engagement_time_msec: 100, // Required for GA4 engagement
    };

    // Add specific properties based on event type
    const properties = event.properties || {};
    const mappedProps = this.sanitizeProperties(properties);

    // GA4 specific mappings
    if ('value' in properties && typeof properties.value === 'number') {
      mappedProps.value = properties.value;
    }

    if ('currency' in properties) {
      mappedProps.currency = properties.currency;
    }

    return {
      ...baseParams,
      ...mappedProps,
    };
  }

  private mapUserTraits(traits: UserTraits): Record<string, any> {
    const mapped: Record<string, any> = {};

    if (traits.plan_type) {
      mapped.subscription_type = traits.plan_type;
    }

    if (traits.created_at) {
      mapped.sign_up_date = traits.created_at;
    }

    if (traits.has_connected_bank !== undefined) {
      mapped.has_bank_connection = traits.has_connected_bank;
    }

    if (traits.total_accounts !== undefined) {
      mapped.account_count = traits.total_accounts;
    }

    if (traits.total_budgets !== undefined) {
      mapped.budget_count = traits.total_budgets;
    }

    if (traits.preferred_currency) {
      mapped.currency_preference = traits.preferred_currency;
    }

    if (traits.referral_source) {
      mapped.acquisition_source = traits.referral_source;
    }

    return mapped;
  }

  private sanitizeProperties(properties: Record<string, any>): Record<string, any> {
    const sanitized: Record<string, any> = {};

    for (const [key, value] of Object.entries(properties)) {
      // Skip null/undefined values
      if (value === null || value === undefined) continue;

      // GA4 parameter name requirements
      const sanitizedKey = key
        .toLowerCase()
        .replace(/[^a-z0-9_]/g, '_')
        .substring(0, 40);

      // Sanitize values
      if (typeof value === 'string') {
        sanitized[sanitizedKey] = value.substring(0, 100); // GA4 limit
      } else if (typeof value === 'number') {
        sanitized[sanitizedKey] = value;
      } else if (typeof value === 'boolean') {
        sanitized[sanitizedKey] = value;
      } else if (Array.isArray(value)) {
        sanitized[sanitizedKey] = value.join(',').substring(0, 100);
      } else if (typeof value === 'object') {
        // Flatten nested objects
        sanitized[sanitizedKey] = JSON.stringify(value).substring(0, 100);
      }
    }

    return sanitized;
  }
}